// WhatsApp.js
import React, { useState, useEffect } from 'react';
import { whatsappApi } from '../api';
import { useSocket } from '../context/SocketContext';
import { Card, PageTitle, Btn, Badge, StatCard, Spinner } from '../components/common/UI';
import { Wifi, WifiOff, RefreshCw, Send, Power, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode'; // Add this import

export default function WhatsApp() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [qrImageUrl, setQrImageUrl] = useState(null); // Add this state
  const { qrCode, setQrCode, whatsappStatus } = useSocket();

  // Generate QR image when raw QR code is received
  useEffect(() => {
    if (qrCode && typeof qrCode === 'string') {
      // Generate QR code image
      QRCode.toDataURL(qrCode, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      }, (err, url) => {
        if (err) {
          console.error('Error generating QR code:', err);
          toast.error('Failed to generate QR code');
          return;
        }
        setQrImageUrl(url);
      });
    } else {
      setQrImageUrl(null);
    }
  }, [qrCode]);

  const loadStatus = async () => {
    try {
      const [s, h] = await Promise.all([
        whatsappApi.status(),
        whatsappApi.history({ limit: 5 }),
      ]);
      setStatus(s.data.data);
      setHistory(h.data.data);
    } catch (_e) {
      toast.error('Failed to load WhatsApp status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  useEffect(() => {
    if (whatsappStatus) {
      setStatus((prev) => Object.assign({}, prev, whatsappStatus));
    }
  }, [whatsappStatus]);

  const action = async (key, fn, msg) => {
    setActionLoading(key);
    try {
      await fn();
      toast.success(msg);
      await loadStatus();
    } catch (err) {
      const d = err.response && err.response.data;
      toast.error((d && d.error) || 'Action failed');
    } finally {
      setActionLoading('');
    }
  };

  const connected = status ? status.connected : false;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div>
      <PageTitle sub="Manage your WhatsApp connection">WhatsApp Connection</PageTitle>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Connection" value={connected ? 'Connected' : 'Offline'} icon={connected ? Wifi : WifiOff} color={connected ? '#25d366' : '#ef4444'} />
        <StatCard label="Reconnect Attempts" value={status ? (status.reconnectAttempts || 0) : 0} icon={RefreshCw} color="#f59e0b" />
        {status && status.deviceInfo && (
          <>
            <StatCard label="Platform" value={status.deviceInfo.platform} icon={Power} color="#3b82f6" />
            <StatCard label="Battery" value={status.deviceInfo.battery ? (status.deviceInfo.battery + '%') : 'N/A'} icon={Clock} />
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Connection Control</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!connected ? (
              <Btn onClick={() => action('connect', whatsappApi.connect, 'QR code requested — scan below!')} disabled={!!actionLoading}>
                {actionLoading === 'connect' ? <Spinner size={14} /> : <Wifi size={14} />}
                Connect WhatsApp
              </Btn>
            ) : (
              <Btn variant="danger" onClick={() => action('disconnect', whatsappApi.disconnect, 'Disconnected')} disabled={!!actionLoading}>
                {actionLoading === 'disconnect' ? <Spinner size={14} /> : <WifiOff size={14} />}
                Disconnect
              </Btn>
            )}
            <Btn variant="warning" onClick={() => action('restart', whatsappApi.restart, 'Client restarted')} disabled={!!actionLoading}>
              {actionLoading === 'restart' ? <Spinner size={14} /> : <RefreshCw size={14} />}
              Restart Client
            </Btn>
            {connected && (
              <Btn variant="secondary" onClick={() => action('test', whatsappApi.testMessage, 'Test message sent!')} disabled={!!actionLoading}>
                {actionLoading === 'test' ? <Spinner size={14} /> : <Send size={14} />}
                Send Test Message
              </Btn>
            )}
          </div>

          {status && status.deviceInfo && (
            <div style={{ marginTop: 20, padding: 14, background: '#0f172a', borderRadius: 8 }}>
              <p style={{ fontSize: 11, color: '#64748b', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Device Info</p>
              {[
                ['Name', status.deviceInfo.pushName],
                ['Phone', status.deviceInfo.phone],
                ['Platform', status.deviceInfo.platform],
                ['WID', status.deviceInfo.wid],
              ].map(function(item) {
                if (!item[1]) return null;
                return (
                  <div key={item[0]} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#64748b', fontSize: 12 }}>{item[0]}</span>
                    <span style={{ color: '#e2e8f0', fontSize: 12 }}>{item[1]}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {qrImageUrl && ( // Use qrImageUrl instead of qrCode
            <Card>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 12 }}>📱 Scan QR Code</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>Open WhatsApp → Linked Devices → Scan this QR</p>
              <div style={{ background: '#fff', padding: 12, borderRadius: 8, display: 'inline-block' }}>
                <img src={qrImageUrl} alt="QR Code" style={{ width: 200, height: 200, display: 'block' }} />
              </div>
              <div style={{ marginTop: 10 }}>
                <Btn size="sm" variant="ghost" onClick={() => {
                  setQrCode(null);
                  setQrImageUrl(null);
                }} style={{ color: '#ef4444' }}>Dismiss</Btn>
              </div>
            </Card>
          )}

          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Recent Sessions</h3>
            {history.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: 13 }}>No session history</p>
            ) : history.map((s, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ color: '#e2e8f0', fontSize: 13 }}>{s.deviceInfo ? s.deviceInfo.pushName : 'Unknown'}</p>
                  <p style={{ color: '#64748b', fontSize: 11 }}>{new Date(s.lastConnected).toLocaleString()}</p>
                </div>
                <Badge color={s.status === 'connected' ? 'green' : 'default'}>{s.status}</Badge>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}